import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AdminHomeButton from "../components/AdminHomeButton";
import AdminPageNavigator from "../components/AdminPageNavigator";
import SiteFooter from "../components/SiteFooter";

const VERIFICATION_TOKEN_KEY = "mysipur_verification_token";

const AdminVerification: React.FC = () => {
  const { verifyAdmin, pendingAdminUser, clearAdminVerification, needsAdminVerification, token } = useAuth();
  const navigate = useNavigate();

  const [code, setCode] = useState("");

  useEffect(() => {
    const hasVerificationToken = sessionStorage.getItem(VERIFICATION_TOKEN_KEY);
    // If already fully authenticated, allow forward navigation and skip login redirect.
    if (token) return;

    if (!needsAdminVerification && !hasVerificationToken) {
      navigate("/login", { replace: true });
    }
  }, [needsAdminVerification, navigate, token]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!code.trim()) throw new Error("Please enter the verification code.");
      await verifyAdmin(code.trim());
      navigate("/admin/submissions");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || "Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    clearAdminVerification();
    navigate("/login");
  };

  const errorId = "admin-verify-error";

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center p-4 overflow-hidden">
      <AdminHomeButton />
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/home/sign-in.png')" }}
      />
      <div className="absolute inset-0 bg-brandBlue/20" />

      <main
        id="main-content"
        tabIndex={-1}
        className="relative z-10 flex w-full max-w-md flex-col items-center outline-none"
      >
        <div className="w-full bg-brandBeige/40 backdrop-blur-md rounded-2xl shadow-card border border-white/30 p-6">
          <h1 className="text-2xl font-extrabold text-heading mb-2">Admin Verification</h1>
          <AdminPageNavigator align="center" />
          <p className="text-body mb-5">
            A verification code was sent. Enter the 6-digit code below.
          </p>
          {pendingAdminUser && (
            <p className="text-sm text-gray-600 mb-4">
              Logging in as: <strong>{pendingAdminUser.email}</strong>
            </p>
          )}

          {error && (
            <div id={errorId} role="alert" aria-live="polite" className="bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded-lg shadow-soft mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} aria-describedby={error ? errorId : undefined}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-heading mb-1" htmlFor="admin-verify-code">
                Verification Code <span className="text-red-700" aria-hidden="true">*</span>
              </label>
              <input
                id="admin-verify-code"
                className="w-full p-2 border border-gray-300 rounded-md text-heading"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                disabled={isLoading}
                autoComplete="one-time-code"
                aria-invalid={error ? true : undefined}
                aria-required
              />
              <span className="sr-only">Format: six digits</span>
            </div>

            <button
              type="submit"
              disabled={isLoading || code.length < 6}
              className="w-full bg-brandOrange hover:bg-orange-600 text-white font-semibold py-3 rounded-2xl shadow-card disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
            >
              {isLoading ? "Verifying..." : "Verify"}
            </button>
          </form>

          <p className="text-sm text-gray-600 mt-5">
            Code expired?{" "}
            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-brandNavy font-semibold hover:underline"
            >
              Back to login
            </button>{" "}
            to receive a new code.
          </p>
        </div>

        <div className="mt-4 w-full flex justify-center">
          <button
            type="button"
            onClick={handleBackToLogin}
            disabled={isLoading}
            className="bg-brandNavy/90 hover:bg-brandNavy text-white font-semibold py-3 px-10 rounded-2xl shadow-soft transition-all duration-200 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back to Login
          </button>
        </div>
      </main>

      <div className="relative z-10 mt-auto w-full max-w-md pb-4">
        <SiteFooter />
      </div>
    </div>
  );
};

export default AdminVerification;
